/**
 * Watchlist query hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { watchlistApi, WatchlistListParams } from '../api/watchlist'
import type { BulkTagAction } from '../api/holdings'
import type { WatchlistItemCreate, WatchlistItemUpdate } from '../types'
import { TAGS_KEY } from './useTags'

export const WATCHLIST_KEY = ['watchlist']

export interface UseWatchlistParams {
  tagIds?: string[]
}

export function useWatchlist(params?: UseWatchlistParams) {
  const queryKey = params
    ? [...WATCHLIST_KEY, params]
    : WATCHLIST_KEY

  return useQuery({
    queryKey,
    queryFn: () => watchlistApi.list(params as WatchlistListParams),
  })
}

export function useWatchlistItem(symbol: string) {
  return useQuery({
    queryKey: [...WATCHLIST_KEY, symbol],
    queryFn: () => watchlistApi.get(symbol),
    enabled: !!symbol,
  })
}

export function useAddToWatchlist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: WatchlistItemCreate) => watchlistApi.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHLIST_KEY })
    },
  })
}

export function useUpdateWatchlistItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ symbol, data }: { symbol: string; data: WatchlistItemUpdate }) =>
      watchlistApi.update(symbol, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHLIST_KEY })
    },
  })
}

export function useRemoveFromWatchlist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (symbol: string) => watchlistApi.remove(symbol),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHLIST_KEY })
    },
  })
}

// Bulk operations

export function useBulkUpdateWatchlistTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      symbols,
      action,
      tagIds,
    }: {
      symbols: string[]
      action: BulkTagAction
      tagIds: string[]
    }) => watchlistApi.bulkUpdateTags(symbols, action, tagIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHLIST_KEY })
      queryClient.invalidateQueries({ queryKey: TAGS_KEY })
    },
  })
}

export function useBulkDeleteWatchlist() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (symbols: string[]) => watchlistApi.bulkDelete(symbols),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHLIST_KEY })
    },
  })
}
